import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
    fetchUserMenuPermissions,
    fetchAllMenuPermissions,
    fetchUserModuleAssignments,
    assignModulesToUser,
    removeModulesFromUser
} from './thunk';

interface MenuPermission {
    id: string;
    menu_key: string;
    menu_label: string;
    menu_icon: string;
    menu_path: string;
    menu_order: number;
    parent_menu: string | null;
    is_active: boolean;
    description?: string;
}

interface UserModuleAssignment {
    id: string;
    user_id: string;
    menu_permission_id: string;
    assigned_by: string;
    assigned_at: string;
    is_active: boolean;
    notes?: string;
    menu_permission?: MenuPermission;
}

interface MenuPermissionsState {
    userMenuPermissions: MenuPermission[];
    allMenuPermissions: MenuPermission[];
    userModuleAssignments: UserModuleAssignment[];
    loading: boolean;
    error: string | null;
    userMenuLoading: boolean;
    userMenuError: string | null;
}

const initialState: MenuPermissionsState = {
    userMenuPermissions: [],
    allMenuPermissions: [],
    userModuleAssignments: [],
    loading: false,
    error: null,
    userMenuLoading: false,
    userMenuError: null,
};

const menuPermissionsSlice = createSlice({
    name: 'menuPermissions',
    initialState,
    reducers: {
        clearError: (state) => {
            state.error = null;
            state.userMenuError = null;
        },
        clearUserMenuPermissions: (state) => {
            state.userMenuPermissions = [];
            state.userMenuError = null;
        },
        clearAllMenuPermissions: (state) => {
            state.allMenuPermissions = [];
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        // Fetch user menu permissions
        builder
            .addCase(fetchUserMenuPermissions.pending, (state) => {
                state.userMenuLoading = true;
                state.userMenuError = null;
            })
            .addCase(fetchUserMenuPermissions.fulfilled, (state, action: PayloadAction<MenuPermission[]>) => {
                state.userMenuLoading = false;
                state.userMenuPermissions = action.payload;
            })
            .addCase(fetchUserMenuPermissions.rejected, (state, action) => {
                state.userMenuLoading = false;
                state.userMenuError = action.payload as string;
            });

        // Fetch all menu permissions
        builder
            .addCase(fetchAllMenuPermissions.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchAllMenuPermissions.fulfilled, (state, action: PayloadAction<MenuPermission[]>) => {
                state.loading = false;
                state.allMenuPermissions = action.payload;
            })
            .addCase(fetchAllMenuPermissions.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            });

        // Fetch user module assignments
        builder
            .addCase(fetchUserModuleAssignments.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchUserModuleAssignments.fulfilled, (state, action: PayloadAction<UserModuleAssignment[]>) => {
                state.loading = false;
                state.userModuleAssignments = action.payload;
            })
            .addCase(fetchUserModuleAssignments.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            });

        // Assign modules to user
        builder
            .addCase(assignModulesToUser.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(assignModulesToUser.fulfilled, (state) => {
                state.loading = false;
            })
            .addCase(assignModulesToUser.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            });

        // Remove modules from user
        builder
            .addCase(removeModulesFromUser.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(removeModulesFromUser.fulfilled, (state) => {
                state.loading = false;
            })
            .addCase(removeModulesFromUser.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            });
    },
});

export const { clearError, clearUserMenuPermissions, clearAllMenuPermissions } = menuPermissionsSlice.actions;
export default menuPermissionsSlice.reducer;
